import { SignupScreen, UserProfile, WorkoutSettings } from 'fitbeat.gym-core';
import { BaseUserProfile, CardioOrResistance, ProgramType } from 'fitbeat.models';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import {
  ScrollView, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { connect } from 'react-redux';
import { SelectionContainer } from '../Bookings/components/SelectionContainer';
import { getUrls } from '../config';
import { IAppStore } from '../HomeWorkouts';
import { GoogleAnalytics } from '../services/google-analytics';
import Theme, { ThemeType } from '../theme';
import { openInAppWebViewerInBackground } from '../utils/inAppWebViewer';
import { defaultScale } from '../utils/scaling';
import { mappedTitles } from '../WorkoutGoalAssistant';
import { getNextStep, navigateToNextStep } from './actions';
import { sharedStyles } from './OnboardingSharedStyles';
import { INavigateToNextStep } from './types';

interface IProps {
  userProfile: UserProfile;
  navigation: any;

  t(key: string): string;

  getNextStep(navigateToNextStep: INavigateToNextStep): void;

  navigateToNextStep(navigateToNextStep: any): void;
}

interface IState {
  longTermGoal: ProgramType | null;
}

class HomeLongTermGoal extends PureComponent<IProps, IState> {
  public state: IState = {
    longTermGoal: null,
  };

  private googleAnalytics = new GoogleAnalytics();

  public setGoalAndNavigateToNextStep = (longTermGoal: ProgramType) => {
    this.setState({ longTermGoal });
    const { userProfile } = this.props;
    this.props.getNextStep({
      currentScreen: SignupScreen.FitnessGoal,
      userProfile: { longTermGoal },
    });
    this.props.navigateToNextStep(this.props.navigation);
    this.googleAnalytics.logEventWithParamaters('sign_up_workout_goal', { longTermGoal });
  }

  public render() {
    const { t } = this.props;
    const { longTermGoal } = this.state;
    const { GOAL_OVERVIEW_NUTRITION_AND_WORKOUT } = getUrls();
    return (
      <ScrollView style={sharedStyles.container}>
        <Text style={[Theme.textStyle.heading, { marginBottom: defaultScale(6) }]}>
          {t('WhatIsYourFitnessGoal')}
        </Text>
        <Text style={[Theme.textStyle.bodyCopyReg, { color: Theme.GREY_MEDIUM, marginBottom: defaultScale(24) }]}>
          {t('personalizeFitnessJourneyGoal')}</Text>
        <View style={{ flex: 1, marginTop: defaultScale(24) }}>
          <SelectionContainer
            title={mappedTitles[ProgramType.GetStrongAndFitGoal]}
            subTitle={t('allRounderWorkouts')}
            isSelected={longTermGoal === ProgramType.GetStrongAndFitGoal}
            theme={ThemeType.light}
            onPress={() => this.setGoalAndNavigateToNextStep(ProgramType.GetStrongAndFitGoal)}
            height={defaultScale(100)}
            width={'100%'}
            titleStyle={sharedStyles.bookingSelectionTitle}
            subTitleStyle={sharedStyles.bookingSelectionSubtitle}
            overrideLightStyles={{ defaultSubtitleColor: Theme.GREY_MEDIUM }}
            style={sharedStyles.selectionContainer}
          />
          <SelectionContainer
            title={mappedTitles[ProgramType.GetLeanAndFitGoal]}
            subTitle={t('leanAndFitWorkouts')}
            style={sharedStyles.selectionContainer}
            titleStyle={sharedStyles.bookingSelectionTitle}
            subTitleStyle={sharedStyles.bookingSelectionSubtitle}
            isSelected={longTermGoal === ProgramType.GetLeanAndFitGoal}
            theme={ThemeType.light}
            onPress={() => this.setGoalAndNavigateToNextStep(ProgramType.GetLeanAndFitGoal)}
            height={defaultScale(100)}
            overrideLightStyles={{ defaultSubtitleColor: Theme.GREY_MEDIUM }}
            width={'100%'}
          />
          <SelectionContainer
            title={mappedTitles[ProgramType.MobilityAndTone]}
            subTitle={t('mobilityAndTone')}
            isSelected={longTermGoal === ProgramType.MobilityAndTone}
            theme={ThemeType.light}
            onPress={() => this.setGoalAndNavigateToNextStep(ProgramType.MobilityAndTone)}
            height={defaultScale(100)}
            width={'100%'}
            titleStyle={sharedStyles.bookingSelectionTitle}
            subTitleStyle={sharedStyles.bookingSelectionSubtitle}
            overrideLightStyles={{ defaultSubtitleColor: Theme.GREY_MEDIUM }}
            style={sharedStyles.selectionContainer}
          />
          <SelectionContainer
            title={mappedTitles[ProgramType.GainMuscleAndStrengthGoal]}
            subTitle={t('muscleAndStrengthWorkouts')}
            isSelected={longTermGoal === ProgramType.GainMuscleAndStrengthGoal}
            theme={ThemeType.light}
            onPress={() => this.setGoalAndNavigateToNextStep(ProgramType.GainMuscleAndStrengthGoal)}
            height={defaultScale(100)}
            width={'100%'}
            titleStyle={sharedStyles.bookingSelectionTitle}
            subTitleStyle={sharedStyles.bookingSelectionSubtitle}
            overrideLightStyles={{ defaultSubtitleColor: Theme.GREY_MEDIUM }}
            style={sharedStyles.selectionContainer}
          />
          <SelectionContainer
            title={mappedTitles[ProgramType.MuscleAndShred]}
            subTitle={t('muscleAndShred')}
            isSelected={longTermGoal === ProgramType.MuscleAndShred}
            theme={ThemeType.light}
            onPress={() => this.setGoalAndNavigateToNextStep(ProgramType.MuscleAndShred)}
            height={defaultScale(100)}
            width={'100%'}
            titleStyle={sharedStyles.bookingSelectionTitle}
            subTitleStyle={sharedStyles.bookingSelectionSubtitle}
            overrideLightStyles={{ defaultSubtitleColor: Theme.GREY_MEDIUM }}
            style={sharedStyles.selectionContainer}
          />
          <TouchableOpacity
            style={styles.learnMoreTextContainer}
            onPress={() => { openInAppWebViewerInBackground(GOAL_OVERVIEW_NUTRITION_AND_WORKOUT); }}>
            <Text style={[styles.learnMoreText, { color: Theme.PRIMARY }]}>{t('learnMore')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    );
  }
}

const styles = StyleSheet.create({
  learnMoreTextContainer: {
    paddingVertical: defaultScale(15),
  },
  learnMoreText: {
    ...Theme.textStyle.bodyCopyBold,
    marginRight: defaultScale(5),
    marginBottom: defaultScale(3),
    alignSelf: 'center',
  },
});

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    userProfile: state.auth.signup.userProfile,
  };
};

const mapDispatchToProps = ({
  getNextStep,
  navigateToNextStep,
});

export const HomeLongTermGoalScreen = translate(['auth'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(HomeLongTermGoal),
);
